import { formatDate, formatDateTime } from '@/lib/date-time';
/**
 * @file components/audit-tasks/lab/LabReportsInlineCard.tsx
 * @description 實驗室報告內聯卡片：列表 Table + 上傳/替換 Drawer + 判讀 Modal（序13）。
 *
 * 範圍：根據 caseId 過濾，僅展示本案件的實驗室報告。
 *
 * 主要互動：
 * 1. 卡片頂部「上傳報告」按鈕開啟右側 Drawer（新增一筆）。
 * 2. 列表每筆提供「判讀」「替換」「刪除」操作（序13 誤傳補救 + 判讀）：
 *    - 判讀：系統判讀（規則式）/ 人工判讀（合格與否 + 判定依據）。
 *    - 替換：復用同一 Drawer，帶入既有值，PATCH 更新（含換檔）。
 *    - 刪除：二次確認後 DELETE；刪後若樣品無其他報告，後端清回樣品結果。
 * 3. 重複上傳提示（序13）：選定樣品已有報告時，Drawer 內顯示 Alert
 *    「該樣品已有檢測報告，繼續將『新增一筆』；如要更正請用列表的『替換』」。
 *
 * 真實 schema 對齊：
 * - reportNumber 不是 reportCode
 * - testItems 是 jsonb 陣列
 * - passOrFail 是 boolean，不是字串枚舉
 * - 判讀欄位：judgmentMode('auto'|'manual') / judgedBy / judgedAt / judgmentBasis
 */

import { useMemo, useState } from 'react';
import { message, modal } from '@/lib/antd-app';
import {
  Alert,
  Button,
  Card,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Plus, Gavel, Replace, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/api-client';
import { uploadFile } from '@/lib/file-upload';
import { FileLink } from '@/components/common/FileLink';

/** 後端 GET /api/cases/:caseId/lab-reports 回傳的單筆結構（已 join sampleCode） */
interface LabReport {
  id: number;
  sampleId: number;
  sampleCode: string;
  fileId: number | null;
  reportNumber: string;
  testItems: unknown[];
  passOrFail: boolean | null;
  notes: string | null;
  /** 判讀方式（序13）：'auto'=系統 / 'manual'=人工 / null=未判讀 */
  judgmentMode: 'auto' | 'manual' | null;
  judgedBy: number | null;
  judgedAt: string | null;
  judgmentBasis: string | null;
  /** 序74：殘留農藥安全容許量標準版本號（判定單序號11） */
  standardVersion: string | null;
  /** 序74：判定者簽名檔 file id */
  judgeSignatureFileId: number | null;
  /** 序74：判定者姓名（判定者＝主導稽核員） */
  judgedByName: string | null;
  /** 序75/76：列表擴欄 */
  clientCode: string | null;
  clientName: string | null;
  sampleName: string | null;
  collectedAt: string | null;
  collectedByName: string | null;
  createdAt: string;
}

/** 採樣下拉選項所需的最小 Sample 結構 */
interface Sample {
  id: number;
  code: string;
  samplingType: string;
}

interface Props {
  /** 當前 audit task 對應的 case id；用於 query 過濾與 mutation URL */
  caseId: number;
}

/** 上傳/替換 Drawer 的表單值 */
interface FormValues {
  sampleId: number;
  reportNumber?: string;
  // 序：上傳時不判讀，passOrFail 由後續「判讀」流程產出，故上傳表單不含此欄
  notes?: string;
}

/** 判讀 Modal 的表單值 */
interface JudgeFormValues {
  mode: 'auto' | 'manual';
  passOrFail?: boolean;
  judgmentBasis?: string;
  /** 序74：殘留農藥安全容許量標準版本號 */
  standardVersion?: string;
  /** 序74：判定者簽名檔 file id（隨 state 帶入，不入表單控件） */
  judgeSignatureFileId?: number;
}

/**
 * 實驗室報告內聯卡片。
 *
 * 用於 audit task 詳情頁 PhaseLab 區塊內，內聯顯示本案件的實驗室報告列表，
 * 並提供上傳/替換/刪除/判讀入口，避免使用者跳轉到獨立頁面。
 */
export function LabReportsInlineCard({ caseId }: Props) {
  const qc = useQueryClient();
  // 上傳/替換 Drawer 開關
  const [open, setOpen] = useState(false);
  // 當前正在「替換」的報告 id（null＝新增一筆）
  const [editingId, setEditingId] = useState<number | null>(null);
  // antd Form 實例（上傳/替換）
  const [form] = Form.useForm<FormValues>();
  // 已上傳的報告檔案 id（OSS 直傳完成後設定，submit 時帶入 body）
  const [fileId, setFileId] = useState<number | null>(null);
  // 判讀 Modal 目標報告（null＝關閉）
  const [judgeTarget, setJudgeTarget] = useState<LabReport | null>(null);
  // 判讀表單實例
  const [judgeForm] = Form.useForm<JudgeFormValues>();

  // 列表查詢：本 case 的實驗室報告
  const reportsQ = useQuery<{ items: LabReport[] }>({
    queryKey: ['lab-reports', caseId],
    queryFn: () => apiClient.get<{ items: LabReport[] }>(`/api/cases/${caseId}/lab-reports`),
  });

  // 採樣下拉選項：僅當 Drawer 開啟時拉取，避免無謂請求
  const samplesQ = useQuery<{ items: Sample[] }>({
    queryKey: ['samples-for-lab', caseId],
    queryFn: () => apiClient.get<{ items: Sample[] }>(`/api/cases/${caseId}/samples`),
    enabled: open,
  });

  // 監聽 Drawer 內選中的 sampleId，用於「重複上傳」提示
  const selectedSampleId = Form.useWatch('sampleId', form);
  // 選中樣品是否已有報告（新增模式下用於提示；替換模式不提示自己）
  const dupExisting = useMemo(() => {
    if (editingId != null || selectedSampleId == null) return false;
    return (reportsQ.data?.items ?? []).some((r) => r.sampleId === selectedSampleId);
  }, [editingId, selectedSampleId, reportsQ.data]);

  // 提交 mutation：新增（POST）或替換（PATCH）
  const submit = useMutation({
    mutationFn: async (values: FormValues) => {
      const body = { ...values, fileId: fileId ?? undefined };
      if (editingId != null) {
        // 替換：PATCH 既有報告
        return apiClient.patch(`/api/cases/${caseId}/lab-reports/${editingId}`, body);
      }
      // 新增一筆
      return apiClient.post(`/api/cases/${caseId}/lab-reports`, body);
    },
    onSuccess: () => {
      message.success(editingId != null ? '報告已替換' : '報告已上傳');
      closeDrawer();
      void qc.invalidateQueries({ queryKey: ['lab-reports', caseId] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  // 刪除 mutation：DELETE
  const del = useMutation({
    mutationFn: async (id: number) => apiClient.delete(`/api/cases/${caseId}/lab-reports/${id}`),
    onSuccess: () => {
      message.success('報告已刪除');
      void qc.invalidateQueries({ queryKey: ['lab-reports', caseId] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  // 判讀 mutation：POST judge（序74：附容許量標準版本號 + 判定者簽名檔）
  const judge = useMutation({
    mutationFn: async (values: JudgeFormValues) => {
      if (!judgeTarget) throw new Error('無判讀目標');
      return apiClient.post(`/api/cases/${caseId}/lab-reports/${judgeTarget.id}/judge`, {
        ...values,
        judgeSignatureFileId: judgeSignatureId ?? undefined,
      });
    },
    onSuccess: () => {
      message.success('判讀完成');
      setJudgeTarget(null);
      judgeForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['lab-reports', caseId] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  /** 開啟「新增一筆」Drawer */
  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    setFileId(null);
    setOpen(true);
  };

  /** 開啟「替換」Drawer，帶入既有值 */
  const openReplace = (r: LabReport) => {
    setEditingId(r.id);
    setFileId(r.fileId ?? null);
    form.setFieldsValue({
      sampleId: r.sampleId,
      reportNumber: r.reportNumber ?? undefined,
      notes: r.notes ?? undefined,
    });
    setOpen(true);
  };

  /** 關閉 Drawer 並重置表單與已上傳檔案 id */
  const closeDrawer = () => {
    setOpen(false);
    setEditingId(null);
    form.resetFields();
    setFileId(null);
  };

  /** 刪除前二次確認 */
  const confirmDelete = (r: LabReport) => {
    modal.confirm({
      title: '刪除檢測報告',
      content: `確認刪除報告「${r.reportNumber || `#${r.id}`}」？此操作無法復原；若該樣品無其他報告，樣品結果將回退為待處理。`,
      okText: '刪除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => del.mutateAsync(r.id),
    });
  };

  /** 開啟判讀 Modal，預填當前判定 */
  const openJudge = (r: LabReport) => {
    setJudgeTarget(r);
    setJudgeSignatureId(r.judgeSignatureFileId ?? null);
    judgeForm.setFieldsValue({
      mode: 'manual',
      passOrFail: r.passOrFail ?? undefined,
      judgmentBasis: r.judgmentBasis ?? undefined,
      standardVersion: r.standardVersion ?? undefined,
    });
  };

  // 序74：判定者簽名檔（導入本人員工簽名檔）
  const [judgeSignatureId, setJudgeSignatureId] = useState<number | null>(null);
  const importJudgeSignature = useMutation({
    mutationFn: async () => apiClient.get<{ fileId: number | null }>('/api/auth/me/signature'),
    onSuccess: (res) => {
      if (res.fileId) {
        setJudgeSignatureId(res.fileId);
        message.success('已導入我的簽名檔');
      } else {
        message.warning('尚未設定簽名檔，請至員工檔案上傳簽名');
      }
    },
    onError: (e: Error) => {
      console.error('[LabReportsInlineCard] import signature failed', e);
      message.error(e.message || '導入簽名檔失敗');
    },
  });

  // 監聽判讀方式：auto 時隱藏「合格與否」（由系統規則產出）
  const judgeMode = Form.useWatch('mode', judgeForm);

  // Table 欄位定義（序76 排序：驗證註冊碼/經營者/樣品名稱/判定結果/採樣編號/採樣日期/
  // 採樣人員/報告日期/報告編號）
  const cols: ColumnsType<LabReport> = [
    {
      title: '驗證註冊碼',
      dataIndex: 'clientCode',
      width: 120,
      render: (v: string | null) => <span style={{ fontFamily: 'monospace' }}>{v ?? '—'}</span>,
    },
    {
      title: '農產品經營業者名稱',
      dataIndex: 'clientName',
      width: 150,
      ellipsis: true,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '樣品名稱',
      dataIndex: 'sampleName',
      width: 130,
      ellipsis: true,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '判定結果',
      dataIndex: 'passOrFail',
      width: 90,
      render: (v: boolean | null) =>
        v == null ? (
          <Tag>未判定</Tag>
        ) : v ? (
          <Tag color="success">通過</Tag>
        ) : (
          <Tag color="error">不通過</Tag>
        ),
    },
    {
      title: '採樣編號',
      dataIndex: 'sampleCode',
      width: 130,
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v}</span>,
    },
    {
      title: '採樣日期',
      dataIndex: 'collectedAt',
      width: 110,
      render: (d: string | null) => (d ? formatDate(d) : '—'),
    },
    {
      title: '採樣人員',
      dataIndex: 'collectedByName',
      width: 100,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '報告日期',
      dataIndex: 'createdAt',
      width: 110,
      render: (d: string) => formatDate(d),
    },
    {
      title: '報告編號',
      dataIndex: 'reportNumber',
      width: 150,
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v || '—'}</span>,
    },
    {
      title: '檢測項目',
      dataIndex: 'testItems',
      width: 90,
      render: (items: unknown[]) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          共 {items?.length ?? 0} 項
        </Typography.Text>
      ),
    },
    {
      // 判讀狀態（序13/74）：未判讀 / 系統判讀 / 人工判讀 + 判定者/依據/標準版本 Tooltip
      title: '檢測判定',
      key: 'judgment',
      width: 130,
      render: (_: unknown, r: LabReport) => {
        if (!r.judgmentMode) return <Tag>未判定</Tag>;
        const label = r.judgmentMode === 'auto' ? '系統判讀' : '人工判定';
        const color = r.judgmentMode === 'auto' ? 'blue' : 'geekblue';
        return (
          <Tooltip
            title={
              <div>
                {r.judgedByName && <div>判定者：{r.judgedByName}</div>}
                {r.standardVersion && <div>容許量標準版本：{r.standardVersion}</div>}
                <div>{r.judgmentBasis || '（無判定依據）'}</div>
                {r.judgeSignatureFileId && <div>已簽字</div>}
                {r.judgedAt && (
                  <div style={{ fontSize: 11, opacity: 0.8 }}>
                    {formatDateTime(r.judgedAt)}
                  </div>
                )}
              </div>
            }
          >
            <Tag color={color}>{label}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '報告檔案',
      dataIndex: 'fileId',
      width: 100,
      render: (id: number | null) => (id ? <FileLink fileId={id} filename="下載" compact /> : '—'),
    },
    {
      title: '上傳時間',
      dataIndex: 'createdAt',
      width: 150,
      render: (d: string) => formatDateTime(d),
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_: unknown, r: LabReport) => (
        <Space size={4}>
          <Button type="link" size="small" icon={<Gavel size={13} />} onClick={() => openJudge(r)}>
            判讀
          </Button>
          <Button
            type="link"
            size="small"
            icon={<Replace size={13} />}
            onClick={() => openReplace(r)}
          >
            替換
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<Trash2 size={13} />}
            onClick={() => confirmDelete(r)}
          >
            刪除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        size="small"
        title="實驗室檢驗報告"
        extra={
          <Button type="primary" size="small" icon={<Plus size={14} />} onClick={openCreate}>
            上傳報告
          </Button>
        }
      >
        {reportsQ.isLoading ? (
          <Typography.Text>載入中…</Typography.Text>
        ) : reportsQ.data?.items.length ? (
          <Table<LabReport>
            rowKey="id"
            dataSource={reportsQ.data.items}
            columns={cols}
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
          />
        ) : (
          <Empty description="尚無檢驗報告" />
        )}
      </Card>

      {/* 上傳 / 替換 Drawer */}
      <Drawer
        title={editingId != null ? '替換實驗室報告' : '上傳實驗室報告'}
        open={open}
        onClose={closeDrawer}
        width={520}
        footer={
          <Space>
            <Button onClick={closeDrawer}>取消</Button>
            <Button type="primary" loading={submit.isPending} onClick={() => form.submit()}>
              {editingId != null ? '儲存替換' : '提交'}
            </Button>
          </Space>
        }
      >
        {/* 重複上傳提示（序13）：新增模式下選中樣品已有報告時提示區分「新增」vs「替換」 */}
        {dupExisting && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="該樣品已有檢測報告"
            description="繼續提交將『新增一筆』（如複驗場景）；若要更正誤傳的報告，請關閉此視窗並在列表中對該報告使用『替換』。"
          />
        )}
        <Form form={form} layout="vertical" onFinish={(v) => submit.mutate(v)}>
          <Form.Item name="sampleId" label="採樣編號" rules={[{ required: true }]}>
            <Select
              // 替換模式下不允許改綁樣品（避免破壞樣品-報告一致性）
              disabled={editingId != null}
              loading={samplesQ.isLoading}
              options={(samplesQ.data?.items ?? []).map((s) => ({
                value: s.id,
                label: `${s.code} (${s.samplingType})`,
              }))}
              placeholder="選擇要關聯的採樣"
            />
          </Form.Item>
          <Form.Item name="reportNumber" label="報告編號">
            <Input placeholder="實驗室出具的報告編號（可選）" />
          </Form.Item>
          {/* 序：上傳報告時不判讀（移除「是否通過」）；上傳後於列表「判讀」再產出判定結果 */}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="上傳報告後再進行判讀"
            description="此步驟僅上傳實驗室報告，不需在此填寫是否通過；上傳後請於列表使用「判讀」進行判定，結果將顯示於「判定結果」。"
          />
          <Form.Item label={editingId != null ? '報告檔案（重新選擇即換檔）' : '報告檔案'}>
            <Upload
              maxCount={1}
              beforeUpload={async (file) => {
                try {
                  // OSS 直傳：scope=case 且 owner 綁定本案件，方便後續歸檔
                  const result = await uploadFile(file, {
                    scope: 'case',
                    ownerType: 'case',
                    ownerId: caseId,
                  });
                  setFileId(result.id);
                  message.success('檔案上傳成功');
                } catch (e) {
                  // 上傳失敗時提示用戶
                  message.error((e as Error).message);
                }
                // 回傳 false 阻止 antd Upload 走預設上傳邏輯
                return false;
              }}
            >
              <Button>選擇檔案</Button>
            </Upload>
            {fileId != null && (
              <Typography.Text type="success" style={{ fontSize: 12, marginLeft: 8 }}>
                已上傳 (#{fileId})
              </Typography.Text>
            )}
          </Form.Item>
          <Form.Item name="notes" label="備注">
            <Input.TextArea rows={3} placeholder="可選" maxLength={5000} showCount />
          </Form.Item>
        </Form>
      </Drawer>

      {/* 判讀 Modal（序13）：系統判讀 / 人工判讀 */}
      <Modal
        title="報告判讀"
        open={judgeTarget != null}
        onCancel={() => {
          setJudgeTarget(null);
          judgeForm.resetFields();
        }}
        onOk={() => judgeForm.submit()}
        confirmLoading={judge.isPending}
        okText="送出判讀"
        cancelText="取消"
      >
        <Form form={judgeForm} layout="vertical" onFinish={(v) => judge.mutate(v)}>
          <Form.Item
            name="mode"
            label="判讀方式"
            initialValue="manual"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Radio.Button value="manual">人工判讀</Radio.Button>
              <Radio.Button value="auto">系統判讀</Radio.Button>
            </Radio.Group>
          </Form.Item>
          {judgeMode === 'auto' ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="系統自動判讀（規則式）"
              description="系統依上傳時填入的檢測項目做規則式判定；報告內容解析（OCR/結構化）尚未接入，若無法判定將回退提示人工。"
            />
          ) : (
            <Form.Item name="passOrFail" label="判定合格與否" rules={[{ required: true }]}>
              <Radio.Group>
                <Radio value={true}>合格（PASS）</Radio>
                <Radio value={false}>不合格（FAIL）</Radio>
              </Radio.Group>
            </Form.Item>
          )}
          {/* 序74：檢測判定單（ZHSR02-37 TOP / ZHSR02-25 TAP）序號11——容許量標準版本號 */}
          <Form.Item
            name="standardVersion"
            label="殘留農藥安全容許量標準版本號"
            tooltip="判定內容參考檢測判定單（TOP：ZHSR02-37 有機檢測判定單(ECO)；TAP：ZHSR02-25 檢測判定單）"
          >
            <Input maxLength={200} placeholder="例：農藥殘留容許量標準 112.06.15 版" />
          </Form.Item>
          <Form.Item name="judgmentBasis" label="判定依據">
            <Input.TextArea
              rows={3}
              maxLength={5000}
              showCount
              placeholder="引用對應採樣單 / 檢測標準 / 表單，說明判定依據（可選）"
            />
          </Form.Item>
          {/* 序74：判定者（主導稽核員）簽字——判定結果+簽字＝通過/不通過；結果將通知管理組轉達客戶 */}
          <Form.Item
            label="判定者簽字"
            tooltip="判定者應為主導稽核員；簽字後判定生效並通知管理組轉達客戶"
          >
            <Space>
              <Button
                type="primary"
                ghost
                size="small"
                loading={importJudgeSignature.isPending}
                onClick={() => importJudgeSignature.mutate()}
              >
                導入簽名檔
              </Button>
              {judgeSignatureId && <Tag color="success">已簽字</Tag>}
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
